import styled from "styled-components";
import {
  ArrowUturnLeftIcon,
  IconButton,
  PencilSquareIcon,
  Tooltip,
  TrashIcon,
  Typography,
  useInterstateToken
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/core-labels";
import type { FeeWithUIState } from "../types";
import { formatCurrency } from "../utils/format";

const RowContainer = styled.div<{
  $disabled?: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr 150px 72px;
  gap: 8px;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  background-color: transparent;
  border-bottom: 1px solid #d1d5db;
  min-height: 48px;
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  transition: opacity 0.2s;

  @media (max-width: 768px) {
    min-width: 450px;
  }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
`;

const AmountCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  padding-left: 32px;
`;

export interface FeeRowProps {
  disabled: boolean;
  fee: FeeWithUIState;
  locale?: string;
  onDelete: (feeId: string) => void;
  onOpenEdit: (feeId: string) => void;
  onRestore: (feeId: string) => void;
}

export const FeeRow = ({
  disabled,
  fee,
  locale = "en-US",
  onDelete,
  onOpenEdit,
  onRestore
}: FeeRowProps) => {
  const getToken = useInterstateToken();
  const errorColor =
    getToken("sem.color.on-surface.error.default") ?? "#c5281c";
  const primaryColor = getToken("sem.color.interactive.default") ?? "#0057b8";

  const handleRowClick = () => {
    if (!disabled && fee.id) {
      onOpenEdit(fee.id);
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && fee.id) {
      if (fee.removed) {
        onRestore(fee.id);
      } else {
        onDelete(fee.id);
      }
    }
  };

  return (
    <RowContainer $disabled={disabled} onClick={handleRowClick}>
      {/* Name */}
      <NameCell>
        <Typography variant={"body-md"}>{fee.name}</Typography>
      </NameCell>

      {/* Amount */}
      <AmountCell>
        <Typography variant={"body-md"}>
          {formatCurrency(fee.amount, locale)}
        </Typography>
      </AmountCell>

      {/* Edit icon + Delete / Restore */}
      <ActionCell>
        <PencilSquareIcon
          aria-hidden
          style={{ width: 16, height: 16, color: primaryColor, flexShrink: 0 }}
          title="Edit"
        />
        <Tooltip
          position="top"
          size="small"
          toolTipContent={
            fee.removed
              ? String(Locale.getText("RESTORE"))
              : String(Locale.getText("DELETE"))
          }
        >
          <IconButton
            aria-label={
              fee.removed
                ? String(Locale.getText("RESTORE"))
                : String(Locale.getText("DELETE"))
            }
            buttonStyle="tertiary"
            disabled={disabled}
            size="sm"
            onClick={handleActionClick}
          >
            {fee.removed ? (
              <ArrowUturnLeftIcon title="Restore" />
            ) : (
              <TrashIcon style={{ color: errorColor }} title="Delete" />
            )}
          </IconButton>
        </Tooltip>
      </ActionCell>
    </RowContainer>
  );
};
